/* ═══════════════════════════════════════════════════════════════════════════
   The identity seam
   ───────────────────────────────────────────────────────────────────────────
   HexClean never handles a password and never sees a code. `hexclean-api`
   authenticates by verifying a Firebase ID token (`POST /auth/firebase`), which
   means the challenge belongs to Firebase and this module is the one place that
   talks to it.

   **Verification is the mailbox.** Two methods are enabled on the project and
   both prove an address:

   · **google**, which the provider has always vouched for.
   · **password**, which it has not until the mailed link is followed — which is
     why `emailVerified` is surfaced below rather than assumed.

   Neither proves a phone number, and an account here is still keyed by one,
   because that is what a job is dispatched to. So the API answers such a
   sign-in with `linkRequired` until the holder says which number they are on —
   *says*, not proves: the SMS challenge is gone, along with the reCAPTCHA
   widget and the pending-confirmation object that used to live in this file.
   What keeps a declared number from naming somebody else's account is on the
   server, in `service.Auth.phoneFor`.

   Registration lives here too, for the same reason sign-in does: creating an
   email account is a Firebase operation and the API only ever sees the token
   it produced. The password is typed into this app and handed straight to the
   SDK — it is never sent to HexClean and never stored on either side, which is
   also why "reset password" is a mailed link rather than a route on our API.

   `VITE_API_MODE=mock` — the default, and what the tests and every development
   run use — never reaches this module; the mock owns its own challenge. In
   `http` mode without a Firebase config, every call throws a named error rather
   than faking a token, because a silently faked token is a sign-in that appears
   to work and produces a 401 on the next screen.
   ═══════════════════════════════════════════════════════════════════════════ */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import {
  GoogleAuthProvider,
  applyActionCode,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as forgetProviderSession,
  verifyPasswordResetCode,
  type ActionCodeSettings,
  type Auth,
} from 'firebase/auth'

import { config } from './config'
import { HexApiError } from './types'

export interface Identity {
  /** Opens the Google account chooser. Resolves to an ID token that names a
   *  Google account and no number. */
  withGoogle(): Promise<string>
  /** Signs in with an address and password already registered on the project. */
  withPassword(email: string, password: string): Promise<string>
  /** Posts the verification mail again, for somebody who never got the first.
   *
   *  Needs the password because Firebase will only mail a link for a user it is
   *  currently holding a session for — the SDK's `sendEmailVerification` takes
   *  a `User`, not an address. Signing in to ask for it is not a workaround: an
   *  endpoint that mailed a link to any address typed into it would be a way to
   *  send mail in this product's name. */
  resendVerification(email: string, password: string): Promise<void>
  /** Registers a new address and password, and posts the verification mail.
   *
   *  Resolves to an ID token of exactly the same kind `withPassword` returns.
   *  A new account is not a different sort of identity — it is one that did not
   *  exist a moment ago — so the API needs no route for it and no way to tell
   *  the two apart. */
  createAccount(email: string, password: string): Promise<string>
  /** Posts a password-reset mail, if that address has an account.
   *
   *  Resolves either way. Which addresses are registered here is not this app's
   *  to disclose — see `resetSilently`. */
  requestPasswordReset(email: string): Promise<void>
  /** Checks a reset code and reports whose address it is for, so the screen can
   *  name the account before asking for a new password. */
  checkPasswordReset(code: string): Promise<string>
  /** Spends a reset code on a new password. */
  completePasswordReset(code: string, password: string): Promise<void>
  /** Spends a verification or address-recovery code. */
  applyCode(code: string): Promise<void>
  /** Drops the provider's own session.
   *
   *  Firebase keeps one in IndexedDB independently of ours, so signing out of
   *  HexClean without this leaves the previous person's provider account signed
   *  in on the device — which on a shared handset is the next person's sign-up
   *  arriving with somebody else's address already attached. */
  forget(): Promise<void>
}

function unconfigured(): never {
  throw new HexApiError(
    'unauthorized',
    'Sign-in is not configured for this build. Set the Firebase credentials, or run with VITE_API_MODE=mock.',
  )
}

/* ── the SDK, built once and only if reached ───────────────────────────────── */

let app: FirebaseApp | undefined
let auth: Auth | undefined

function client(): Auth {
  if (config.firebase === null) unconfigured()
  if (auth === undefined) {
    app = initializeApp(config.firebase)
    auth = getAuth(app)
    /* Follow the device rather than the project default, so a mailed link
       arrives in the language the customer is already reading the app in. */
    auth.useDeviceLanguage()
  }
  return auth
}

/* ── where a mailed link comes back to ─────────────────────────────────────── */

/** Where Firebase sends somebody after they have spent a mailed code.
 *
 *  Two settings are involved and they are worth keeping apart. This is the
 *  *continue* URL — the "back to the app" link on whichever page handled the
 *  code. Which page that is belongs to the console (Authentication → Templates
 *  → customise action URL), and pointing it at `/auth/action` is what moves the
 *  work from the Firebase-hosted screen to the branded one in this app. Both
 *  are handled, so that console setting can be flipped either way without a
 *  deploy and nothing breaks in between. */
function continueTo(path: string): ActionCodeSettings {
  return { url: new URL(path, window.location.origin).toString(), handleCodeInApp: false }
}

/** Runs a mail send, and runs it again without the continue URL if that is the
 *  part that was refused.
 *
 *  `auth/unauthorized-continue-uri` means this deploy's own origin is not on the
 *  project's authorised-domains list — a console omission, and the usual one on
 *  a freshly attached custom domain. The mail is worth more than the return
 *  link: without this the customer gets no reset mail at all and the only trace
 *  is a console error nobody is watching. */
async function withContinue(
  send: (settings?: ActionCodeSettings) => Promise<void>,
  path: string,
): Promise<void> {
  try {
    await send(continueTo(path))
  } catch (err) {
    const code = errorCode(err)
    if (code !== 'auth/unauthorized-continue-uri' && code !== 'auth/invalid-continue-uri') throw err
    await send(undefined)
  }
}

/* ── failure ───────────────────────────────────────────────────────────────── */

function errorCode(err: unknown): string {
  return typeof err === 'object' && err !== null && 'code' in err ? String(err.code) : ''
}

/** The provider codes a customer can act on, and what to tell them.
 *
 *  Anything absent keeps the SDK's own message, which is more use in a bug
 *  report than a generic one. Note what is *not* distinguished: a wrong
 *  password, an unknown address and an address registered under a different
 *  method all say the same sentence, because a sign-in screen that tells them
 *  apart is an account-enumeration oracle with a friendly tone. */
const MESSAGES: Record<string, string> = {
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
  'auth/popup-closed-by-user': 'The Google window was closed before sign-in finished.',
  'auth/cancelled-popup-request': 'The Google window was closed before sign-in finished.',
  'auth/popup-blocked': 'The browser blocked the Google window. Allow pop-ups and try again.',
  'auth/account-exists-with-different-credential':
    'That address is already registered with a different sign-in method.',
  'auth/invalid-credential': 'That email and password do not match an account.',
  'auth/wrong-password': 'That email and password do not match an account.',
  'auth/user-not-found': 'That email and password do not match an account.',
  'auth/user-disabled': 'That account has been disabled.',
  'auth/network-request-failed': 'No connection. Check your network and try again.',
  'auth/email-already-in-use': 'That email already has an account. Sign in instead.',
  'auth/invalid-email': 'That does not look like an email address.',
  'auth/missing-password': 'Enter a password.',
  'auth/weak-password': 'Pick a longer password — at least 8 characters.',
  'auth/operation-not-allowed': 'Email sign-in is not enabled for this app.',
  'auth/expired-action-code': 'That link has expired. Ask for a new one.',
  'auth/invalid-action-code': 'That link has already been used, or is not valid.',
  'auth/requires-recent-login': 'Sign in again before changing this.',
}

/** Turns an SDK rejection into the app's own error type. */
function failed(err: unknown): never {
  const message =
    MESSAGES[errorCode(err)] ??
    (err instanceof Error && err.message !== '' ? err.message : 'Sign-in could not be completed.')

  throw new HexApiError('unauthorized', message)
}

/** The one failure a screen has to branch on rather than merely print.
 *
 *  "That address is taken" has an obvious next move — sign in instead of
 *  signing up — and the screen offers the button rather than leaving somebody
 *  to work it out. Carried as the error's `field` so the branch is on a value
 *  and not on matching an English sentence. */
export const EMAIL_TAKEN = 'email-taken'

/** Registration fails as a `validation` error, not an `unauthorized` one.
 *
 *  Nothing here is a refused credential: the address is malformed, or taken, or
 *  the password is too short. Reporting those as `unauthorized` would put them
 *  through the client's 401 handling, which signs the customer out of a session
 *  they were in the middle of creating. */
function failedSignUp(err: unknown): never {
  const code = errorCode(err)
  const field =
    code === 'auth/email-already-in-use'
      ? EMAIL_TAKEN
      : code === 'auth/invalid-email'
        ? 'email'
        : code === 'auth/weak-password' || code === 'auth/missing-password'
          ? 'password'
          : undefined
  throw new HexApiError('validation', MESSAGES[code] ?? 'Your account could not be created.', field)
}

/** Swallows the rejections that would tell a stranger whether an address is
 *  registered here, and re-throws the ones that would not.
 *
 *  Firebase's own enumeration protection already answers "sent" for an unknown
 *  address on projects that have it switched on. This is the same answer for
 *  the projects and older SDK paths that do not, so the screen can say "if that
 *  address has an account, the link is on its way" and have it be true rather
 *  than a comforting fiction one error message away from being contradicted.
 *
 *  A rate limit is deliberately *not* swallowed: it is a fact about this device
 *  rather than about that address, and hiding it leaves somebody tapping a
 *  button that has quietly stopped working. */
function resetSilently(err: unknown): void {
  const code = errorCode(err)
  if (code === 'auth/user-not-found' || code === 'auth/invalid-recipient-email') return
  if (err instanceof HexApiError) throw err
  failed(err)
}

/* ── the implementation ────────────────────────────────────────────────────── */

export const identity: Identity = {
  async withGoogle() {
    try {
      const provider = new GoogleAuthProvider()
      /* Always ask which account. Without it the SDK reuses whichever Google
         session the browser already has, which on a shared handset signs the
         previous person in. */
      provider.setCustomParameters({ prompt: 'select_account' })
      const credential = await signInWithPopup(client(), provider)
      return await credential.user.getIdToken()
    } catch (err) {
      if (err instanceof HexApiError) throw err
      failed(err)
    }
  },

  async withPassword(email, password) {
    try {
      const credential = await signInWithEmailAndPassword(client(), email, password)
      return await credential.user.getIdToken()
    } catch (err) {
      if (err instanceof HexApiError) throw err
      failed(err)
    }
  },

  async createAccount(email, password) {
    try {
      const credential = await createUserWithEmailAndPassword(client(), email, password)
      const token = await credential.user.getIdToken()

      /* Posted from here rather than from a screen, and deliberately outside
         the failure path: the account exists, the token is good, and the
         customer is mid-registration. A mail server having a bad minute is not
         a reason to fail a registration that succeeded — and a registration
         reported as failed after the account was created is one that can never
         be retried, because the address is now taken by the attempt itself. */
      void withContinue(
        (settings) => sendEmailVerification(credential.user, settings),
        '/auth?verified=1',
      ).catch(() => undefined)

      return token
    } catch (err) {
      if (err instanceof HexApiError) throw err
      failedSignUp(err)
    }
  },

  async resendVerification(email, password) {
    try {
      /* Signed in first, because the SDK mails a link for a `User` rather than
         for an address — see the note on the interface. The session this
         creates is the provider's own and is dropped by `forget()` at sign-out
         like any other. */
      const credential = await signInWithEmailAndPassword(client(), email, password)
      if (credential.user.emailVerified) return
      await withContinue(
        (settings) => sendEmailVerification(credential.user, settings),
        '/auth?verified=1',
      )
    } catch (err) {
      if (err instanceof HexApiError) throw err
      failed(err)
    }
  },

  async requestPasswordReset(email) {
    try {
      await withContinue((settings) => sendPasswordResetEmail(client(), email, settings), '/auth')
    } catch (err) {
      resetSilently(err)
    }
  },

  async checkPasswordReset(code) {
    try {
      return await verifyPasswordResetCode(client(), code)
    } catch (err) {
      failed(err)
    }
  },

  async completePasswordReset(code, password) {
    try {
      await confirmPasswordReset(client(), code, password)
    } catch (err) {
      /* A password the project's policy refuses is the customer's to fix, and
         belongs against the field they are typing into rather than as another
         "sign in again" that would throw the reset away. */
      if (errorCode(err) === 'auth/weak-password') {
        throw new HexApiError('validation', MESSAGES['auth/weak-password'] ?? '', 'password')
      }
      failed(err)
    }
  },

  async applyCode(code) {
    try {
      await applyActionCode(client(), code)
    } catch (err) {
      failed(err)
    }
  },

  async forget() {
    /* Reads the module's own handle rather than calling `client()`: signing out
       must not be the thing that builds the SDK, and a session that was never
       started has nothing to drop. */
    if (auth === undefined) return
    await forgetProviderSession(auth).catch(() => undefined)
  },
}
